import React, { useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Clock3,
  Timer,
  UserRound,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { biAPI } from '../../services/api';
import { nepalTodayKey, shiftDateKey } from './biDates';
import { chartAxisTick, chartTooltipStyle, formatNumber, formatPct } from './biTheme';
import BiFilters from './BiFilters';
import BiKpiCard from './BiKpiCard';
import { getFunnelStageMeta } from './funnelStageMeta';
import { useBiVendorId } from './useBiVendorId';
import useDebouncedValue from './useDebouncedValue';
import VerticalWavyFunnel from './VerticalWavyFunnel';
import ActiveUsersStickinessPanel from './ActiveUsersStickinessPanel';

const BLUE = '#3b82f6';
const PURPLE = '#8b5cf6';
const GREEN = '#10b981';


const DEFAULT_STAGE_COLUMNS = [
  { key: 'visits', label: 'App Visits' },
  { key: 'vendorViews', label: 'Store Views' },
  { key: 'cart', label: 'Add to Cart' },
  { key: 'checkout', label: 'Checkout Started' },
  { key: 'placed', label: 'Orders Created' },
  { key: 'delivered', label: 'Orders Fulfilled' },
];

const defaultTo = () => nepalTodayKey();
const defaultFrom = () => shiftDateKey(nepalTodayKey(), -13);

const DeltaLine = ({ value, label }) => {
  if (value == null || !Number.isFinite(value)) return null;
  const up = value >= 0;
  return (
    <p className={`text-[clamp(0.88rem,1vw,0.95rem)] font-semibold ${up ? 'text-emerald-600' : 'text-rose-500'}`}>
      {up ? '↑' : '↓'} {Math.abs(value).toFixed(1)}% {label}
    </p>
  );
};

const Panel = ({ title, action, children, className = '' }) => (
  <div className={`rounded-xl border border-slate-100 bg-white p-[clamp(0.7rem,1vw,1rem)] shadow-[0_6px_18px_rgba(15,23,42,0.04)] ${className}`}>
    <div className="mb-2.5 flex min-h-[1.4rem] flex-wrap items-center justify-between gap-2">
      <h3 className="text-[clamp(1.05rem,1.2vw,1.15rem)] font-semibold text-slate-900">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const getSortValue = (row, columnKey, tableMode) => {
  if (columnKey === 'label') return String(row.label || '');
  if (tableMode === 'cr') {
    if (row.conversionRates && row.conversionRates[columnKey] != null) {
      return Number(row.conversionRates[columnKey]);
    }
    const visits = Number(row.visits) || 0;
    const count = Number(row[columnKey]) || 0;
    return visits > 0 ? (count / visits) * 100 : Number.NEGATIVE_INFINITY;
  }
  return Number(row[columnKey]) || 0;
};

const resolveCr = (row, stageKey) => {
  if (row.conversionRates && row.conversionRates[stageKey] != null) {
    return Number(row.conversionRates[stageKey]);
  }
  const visits = Number(row.visits) || 0;
  if (visits <= 0) return null;
  return ((Number(row[stageKey]) || 0) / visits) * 100;
};

const composedTooltipFormatter = (value, name) => {
  const key = String(name || '');
  if (key === 'conversionRate' || key === 'Conversion rate') {
    return [formatPct(value), 'Conversion rate'];
  }
  if (key === 'vendorViews' || key === 'Store views') {
    return [formatNumber(value), 'Store views'];
  }
  if (key === 'ordersCreated' || key === 'Orders created' || key === 'placed') {
    return [formatNumber(value), 'Orders created'];
  }
  return [formatNumber(value), key];
};

const FunnelAnalysis = () => {
  const { vendorId } = useBiVendorId();
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [weekdays, setWeekdays] = useState([]);
  const [period, setPeriod] = useState('daily');
  const [tableMode, setTableMode] = useState('count');
  const [sortKey, setSortKey] = useState('label');
  const [sortDir, setSortDir] = useState('asc');
  const debouncedFrom = useDebouncedValue(from, 450);
  const debouncedTo = useDebouncedValue(to, 450);
  const debouncedWeekdays = useDebouncedValue(weekdays, 450);

  const rangeError = debouncedFrom && debouncedTo && debouncedFrom > debouncedTo
    ? 'From date must be on or before To date.'
    : null;

  const queryParams = useMemo(() => ({
    vendorId,
    from: debouncedFrom,
    to: debouncedTo,
    period,
    weekdays: debouncedWeekdays.join(','),
  }), [vendorId, debouncedFrom, debouncedTo, period, debouncedWeekdays]);

  // The Refresh button bypasses the API's short-lived result cache.
  const forceRefreshRef = useRef(false);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch: refetchFunnel,
    isFetching,
    isPreviousData,
  } = useQuery(
    ['bi-funnel', queryParams],
    async ({ signal }) => {
      const refresh = forceRefreshRef.current;
      forceRefreshRef.current = false;
      // One request for the whole tab. Using `signal` lets react-query cancel it
      // when the filters change, so stale requests never queue ahead of new ones.
      const response = await biAPI.getFunnel(
        { ...queryParams, section: 'all', ...(refresh ? { refresh: 1 } : {}) },
        { signal }
      );
      return response.data?.data || response.data;
    },
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      enabled: !rangeError,
    }
  );

  const refetch = () => {
    forceRefreshRef.current = true;
    refetchFunnel();
  };
  const isUpdating = isFetching && isPreviousData;

  const stages = data?.stages || [];
  const conversionTrend = data?.conversionTrend || [];
  const activeUserMetrics = data?.activeUserMetrics || null;
  const summary = data?.summary || {};
  const deviceShare = data?.deviceShare || [];
  const performance = data?.performance || [];
  const dropOffPoints = data?.dropOffPoints || [];
  const stageColumns = data?.stageColumns?.length ? data.stageColumns : DEFAULT_STAGE_COLUMNS;
  const bottleneck = data?.dropOffExplanation;

  const kpis = useMemo(() => {
    const apiKpis = data?.kpis?.length ? data.kpis : stages.map((s) => ({
      key: s.key,
      label: s.label,
      value: s.count,
      vsPrevPeriod: s.vsPrevPeriod,
      vsAvgPrev4: s.vsAvgPrev4,
      spark: [],
    }));
    return apiKpis.map((kpi) => {
      const spark = (kpi.spark && kpi.spark.length >= 2)
        ? kpi.spark
        : conversionTrend.map((row) => Number(row[kpi.key] || 0));
      return { ...kpi, spark };
    });
  }, [data?.kpis, stages, conversionTrend]);

  const sortedRows = useMemo(() => {
    const rows = [...conversionTrend];
    rows.sort((a, b) => {
      const av = getSortValue(a, sortKey, tableMode);
      const bv = getSortValue(b, sortKey, tableMode);
      let cmp = 0;
      if (typeof av === 'string' || typeof bv === 'string') {
        cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      } else {
        cmp = av - bv;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [conversionTrend, sortKey, sortDir, tableMode]);

  const totalsRow = useMemo(() => {
    const totals = { label: 'Total' };
    stageColumns.forEach((stage) => {
      totals[stage.key] = conversionTrend.reduce((sum, row) => sum + Number(row[stage.key] || 0), 0);
    });
    return totals;
  }, [conversionTrend, stageColumns]);

  const deviceOrderTotal = useMemo(
    () => deviceShare.reduce((sum, row) => sum + Number(row.orders || 0), 0),
    [deviceShare]
  );

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'label' ? 'asc' : 'desc');
  };

  const SortIcon = ({ columnKey }) => {
    if (sortKey !== columnKey) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-bold text-gray-900">User Funnel</h2>
        <BiFilters
          inline
          showStore={false}
          from={from}
          to={to}
          onFromChange={setFrom}
          onToChange={setTo}
          weekdays={weekdays}
          onWeekdaysChange={setWeekdays}
          onRefresh={() => refetch()}
          refreshing={isFetching}
        />
      </div>

      {isLoading ? <div className="rounded-xl border border-slate-100 bg-white p-3 text-sm text-gray-500">Loading funnel…</div> : null}
      {rangeError ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{rangeError}</div>
      ) : null}
      {isUpdating ? (
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm font-medium text-sky-800">
          Applying filters… previous results are shown until the new numbers load.
        </div>
      ) : null}
      {isError ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          Failed to load funnel: {error?.response?.data?.message || error?.message || 'Unknown error'}
        </div>
      ) : null}

      {!isLoading && !isError ? (
        <div className={`space-y-2 transition-opacity ${isUpdating ? 'opacity-60' : ''}`} aria-busy={isUpdating}>
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-6">
            {kpis.map((kpi, index) => {
              const meta = getFunnelStageMeta(kpi.key);
              return (
                <BiKpiCard
                  key={kpi.key}
                  icon={meta.icon}
                  label={kpi.label}
                  value={formatNumber(kpi.value)}
                  color={meta.color}
                  sparkValues={kpi.spark}
                  waveVariant={index}
                  deltas={{
                    vsPrev: kpi.vsPrevPeriod,
                    vsAvg: kpi.vsAvgPrev4,
                  }}
                />
              );
            })}
          </div>

          <ActiveUsersStickinessPanel activeUserMetrics={activeUserMetrics} isRefreshing={isFetching} />

          <div className="grid grid-cols-12 gap-[clamp(0.5rem,0.8vw,0.85rem)]">
            <Panel
              className="col-span-12 xl:col-span-7"
              title="Conversion Funnel"
              action={bottleneck ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-1 text-sm font-semibold text-rose-800">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Largest drop-off: {bottleneck.stageLabel} (−{formatPct(bottleneck.dropOffPct)})
                </span>
              ) : null}
            >
              <VerticalWavyFunnel stages={stages} bottleneckKey={bottleneck?.stageKey || null} />
              <div className="mt-3 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3 sm:grid-cols-4">
                <div className="rounded-lg bg-slate-50 px-3 py-2.5">
                  <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">Overall conversion rate</p>
                  <p className="mt-1 text-xl font-bold text-slate-900">
                    {formatPct(summary.overallConversion, 2)}
                  </p>
                  <p className="mt-0.5 text-sm text-slate-500">
                    App DAU → Orders Created
                  </p>
                  <p className="mt-1 text-sm font-semibold text-slate-700">
                    {formatPct(summary.overallConversionCompleted, 2)}
                    <span className="ml-1 text-sm font-medium text-slate-500">DAU → Completed</span>
                  </p>
                </div>
                <div className="rounded-lg bg-slate-50 px-3 py-2.5">
                  <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">Total drop-offs</p>
                  <p className="mt-1 text-xl font-bold text-slate-900">
                    {formatNumber(summary.totalDropOffs || 0)}
                  </p>
                  <p className="mt-0.5 text-sm text-slate-500">
                    App DAU − Orders Completed
                  </p>
                  <p className="mt-1 text-sm font-semibold text-slate-700">
                    {formatPct(summary.dropOffRatio, 1)}
                    <span className="ml-1 text-sm font-medium text-slate-500">drop-off ratio</span>
                  </p>
                </div>
                <div className="rounded-lg bg-slate-50 px-3 py-2.5">
                  <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">Average time to convert</p>
                  <p className="mt-1 text-xl font-bold text-slate-900">
                    {summary.avgTimeToConvert?.label || '—'}
                  </p>
                  <p className="mt-0.5 text-sm text-slate-500">
                    {summary.avgTimeSource === 'homepage→order'
                      ? 'Homepage → Order created'
                      : 'Store view → Order created'}
                  </p>
                </div>
                <div className="rounded-lg bg-slate-50 px-3 py-2.5">
                  <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">Funnel completion time</p>
                  <p className="mt-1 text-xl font-bold text-slate-900">
                    {summary.funnelCompletionDays || '—'} days
                  </p>
                  <p className="mt-0.5 text-sm text-slate-500">Selected date-range length</p>
                </div>
              </div>
            </Panel>

            <Panel
              className="col-span-12 xl:col-span-5"
              title={`Stage Table (${period})`}
              action={(
                <div className="flex flex-wrap items-center gap-2">
                  <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                    <button
                      type="button"
                      onClick={() => setTableMode('count')}
                      className={`rounded-md px-2.5 py-1 text-sm font-semibold ${
                        tableMode === 'count' ? 'bg-white text-sky-900 shadow-sm' : 'text-slate-500'
                      }`}
                    >
                      UserCount
                    </button>
                    <button
                      type="button"
                      onClick={() => setTableMode('cr')}
                      className={`rounded-md px-2.5 py-1 text-sm font-semibold ${
                        tableMode === 'cr' ? 'bg-white text-sky-900 shadow-sm' : 'text-slate-500'
                      }`}
                    >
                      CR
                    </button>
                  </div>
                  <select
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                    className="input py-1 text-sm"
                    aria-label="Trend view"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              )}
            >
              <div className="max-h-[28rem] overflow-auto">
                {sortedRows.length ? (
                  <table className="min-w-full text-left text-[clamp(0.95rem,1.1vw,1.05rem)]">
                    <thead className="sticky top-0 bg-white text-sm uppercase text-gray-500 shadow-sm">
                      <tr>
                        <th className="px-2 py-1.5">
                          <button type="button" onClick={() => toggleSort('label')} className="inline-flex items-center gap-1 font-semibold">
                            Date <SortIcon columnKey="label" />
                          </button>
                        </th>
                        {stageColumns.map((stage) => (
                          <th key={stage.key} className="px-2 py-1.5 whitespace-nowrap text-right">
                            <button type="button" onClick={() => toggleSort(stage.key)} className="inline-flex items-center gap-1 font-semibold">
                              {stage.label}{tableMode === 'cr' ? ' CR' : ''}
                              <SortIcon columnKey={stage.key} />
                            </button>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map((row) => (
                        <tr key={row.label} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-2 py-1.5 font-medium text-gray-900 whitespace-nowrap">{row.label}</td>
                          {stageColumns.map((stage) => {
                            const cr = resolveCr(row, stage.key);
                            return (
                              <td key={stage.key} className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                                {tableMode === 'cr'
                                  ? (cr == null ? '—' : formatPct(cr, cr < 1 ? 2 : 1))
                                  : formatNumber(row[stage.key] || 0)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                    {tableMode === 'count' ? (
                      <tfoot>
                        <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                          <td className="px-2 py-1.5 text-slate-800">Total</td>
                          {stageColumns.map((stage) => (
                            <td key={stage.key} className="px-2 py-1.5 text-right tabular-nums text-slate-800">
                              {formatNumber(totalsRow[stage.key] || 0)}
                            </td>
                          ))}
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                ) : (
                  <p className="text-sm text-gray-500">No date rows in this range.</p>
                )}
              </div>
            </Panel>
          </div>

          <div className="grid grid-cols-2 gap-[clamp(0.45rem,0.75vw,0.7rem)] xl:grid-cols-5">
            {performance.map((item) => {
              const Icon = item.key.includes('Time') || item.key.includes('avg')
                ? Timer
                : item.key === 'returningUsers'
                  ? UserRound
                  : item.key === 'bounceRate'
                    ? Clock3
                    : AlertTriangle;
              return (
                <div
                  key={item.key}
                  className="rounded-xl border border-slate-100 bg-white p-[clamp(0.65rem,0.95vw,0.9rem)] shadow-[0_6px_18px_rgba(15,23,42,0.04)]"
                  title={item.how || undefined}
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="mt-2 text-[clamp(0.92rem,1.05vw,1rem)] font-medium text-slate-500">{item.label}</p>
                  <p className="mt-1 text-[clamp(1.45rem,1.75vw,1.75rem)] font-bold leading-none text-slate-900">
                    {item.valueLabel}
                  </p>
                  {(item.totalLabel || item.averageLabel) ? (
                    <div className="mt-1.5 space-y-0.5 text-[clamp(0.82rem,0.95vw,0.9rem)] font-medium text-slate-600">
                      {item.totalLabel ? <p>{item.totalLabel}</p> : null}
                      {item.averageLabel ? <p>{item.averageLabel}</p> : null}
                    </div>
                  ) : null}
                  {item.vsPrevPeriod != null ? (
                    <p className={`mt-1.5 text-[clamp(0.82rem,0.95vw,0.9rem)] font-semibold ${
                      (item.key === 'bounceRate' || item.key === 'abandonmentRate')
                        ? (item.vsPrevPeriod <= 0 ? 'text-emerald-700' : 'text-rose-700')
                        : (item.vsPrevPeriod >= 0 ? 'text-emerald-700' : 'text-rose-700')
                    }`}
                    >
                      {item.vsPrevPeriod >= 0 ? '↑' : '↓'} {Math.abs(item.vsPrevPeriod).toFixed(1)}% vs prev period
                    </p>
                  ) : null}
                  {item.deltaLabel ? (
                    <p className="mt-1.5 text-[clamp(0.82rem,0.95vw,0.9rem)] font-medium text-slate-500">{item.deltaLabel}</p>
                  ) : item.how ? (
                    <p className="mt-1.5 line-clamp-2 text-[clamp(0.82rem,0.95vw,0.9rem)] leading-snug text-slate-400">{item.how}</p>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-12 gap-[clamp(0.5rem,0.8vw,0.85rem)]">
            <Panel className="col-span-12 xl:col-span-6" title="Store views vs orders created">
              <div className="h-[clamp(14rem,28vh,18rem)]">
                {conversionTrend.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={conversionTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" stroke="#64748b" tick={chartAxisTick} />
                      <YAxis yAxisId="left" stroke="#64748b" tick={chartAxisTick} />
                      <YAxis yAxisId="right" orientation="right" stroke={GREEN} tick={chartAxisTick} unit="%" />
                      <Tooltip
                        contentStyle={chartTooltipStyle}
                        labelFormatter={(label) => String(label)}
                        formatter={composedTooltipFormatter}
                      />
                      <Legend wrapperStyle={{ fontSize: 14 }} />
                      <Bar yAxisId="left" dataKey="vendorViews" name="Store views" fill="#93c5fd" radius={[4, 4, 0, 0]} />
                      <Bar yAxisId="left" dataKey="ordersCreated" name="Orders created" fill={PURPLE} radius={[4, 4, 0, 0]} />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="conversionRate"
                        name="Conversion rate"
                        stroke={GREEN}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-gray-500">No trend points in this range.</p>
                )}
              </div>
            </Panel>

            <Panel className="col-span-12 sm:col-span-6 xl:col-span-3" title="Conversions by device">
              <div className="flex h-[clamp(14rem,28vh,18rem)] items-center gap-2">
                {deviceShare.length ? (
                  <>
                    <div className="relative h-full w-[52%] min-w-[120px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={deviceShare}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius="50%"
                            outerRadius="78%"
                            paddingAngle={2}
                            stroke="#fff"
                            strokeWidth={3}
                          >
                            {deviceShare.map((entry) => (
                              <Cell key={entry.name} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={chartTooltipStyle}
                            formatter={(_value, _n, item) => {
                              const cr = Number(item.payload.visits) > 0
                                ? item.payload.conversionRate
                                : item.payload.viewToOrderRate;
                              const basis = Number(item.payload.visits) > 0 ? 'visits' : 'views';
                              return [
                                `${formatPct(cr, 2)} CR (${basis}) · ${formatNumber(item.payload.orders)} orders · ${formatNumber(item.payload.visits)} visits · ${formatNumber(item.payload.vendorViews)} views`,
                                item.payload.name,
                              ];
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-sm font-medium text-slate-500">Total</span>
                        <span className="text-base font-bold text-slate-900">{formatNumber(deviceOrderTotal)}</span>
                        <span className="text-sm text-slate-400">Orders</span>
                      </div>
                    </div>
                    <ul className="flex flex-1 flex-col justify-center gap-2 text-[clamp(0.92rem,1.05vw,1rem)]">
                      {deviceShare.map((entry) => {
                        const cr = Number(entry.visits) > 0 ? entry.conversionRate : entry.viewToOrderRate;
                        const basis = Number(entry.visits) > 0
                          ? `${formatNumber(entry.orders)} / ${formatNumber(entry.visits)} visits`
                          : `${formatNumber(entry.orders)} / ${formatNumber(entry.vendorViews)} views`;
                        return (
                          <li key={entry.name} className="space-y-0.5">
                            <div className="flex items-center justify-between gap-2">
                              <span className="flex items-center gap-1.5 font-semibold text-slate-700">
                                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                                {entry.name}
                              </span>
                              <span className="font-bold tabular-nums text-slate-900">{entry.value}%</span>
                            </div>
                            <p className="pl-4 text-sm text-slate-500">
                              CR {formatPct(cr, 2)} · {basis}
                            </p>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                ) : (
                  <p className="text-sm text-gray-500">No device conversion data in this range.</p>
                )}
              </div>
            </Panel>

            <div className="col-span-12 flex flex-col gap-[clamp(0.5rem,0.8vw,0.85rem)] sm:col-span-6 xl:col-span-3">
              <Panel title="Conversion rate trend">
                <div className="mb-1">
                  <p className="text-[clamp(1.55rem,2vw,2rem)] font-bold leading-none text-slate-900">
                    {formatPct(summary.overallConversion, 2)}
                  </p>
                  <DeltaLine value={summary.overallConversionDelta} label="vs prev. period" />
                </div>
                <div className="h-28">
                  {conversionTrend.length > 1 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={conversionTrend}>
                        <defs>
                          <linearGradient id="crFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={GREEN} stopOpacity={0.35} />
                            <stop offset="100%" stopColor={GREEN} stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="label" hide />
                        <YAxis hide domain={['auto', 'auto']} />
                        <Tooltip
                          contentStyle={chartTooltipStyle}
                          labelFormatter={(label) => String(label)}
                          formatter={(value) => [formatPct(value, 2), 'Visit → Order CR']}
                        />
                        <Area
                          type="monotone"
                          dataKey="visitToOrderRate"
                          name="Visit → Order CR"
                          stroke={GREEN}
                          fill="url(#crFill)"
                          strokeWidth={2}
                          dot={{ r: 2.5 }}
                          activeDot={{ r: 4 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm text-slate-400">Not enough points for trend.</p>
                  )}
                </div>
              </Panel>

              <Panel title="Top drop-off points">
                {dropOffPoints.length ? (
                  <ul className="space-y-2">
                    {dropOffPoints.slice(0, 4).map((item) => (
                      <li key={`${item.fromKey}-${item.toKey}`} className="flex items-center justify-between gap-2 text-[clamp(0.85rem,1vw,0.95rem)]">
                        <span className="min-w-0 truncate text-slate-600">
                          {item.fromLabel} → {item.toLabel}
                        </span>
                        <span className="shrink-0 font-semibold tabular-nums text-rose-600">−{formatPct(item.dropOff)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">No drop-offs detected.</p>
                )}
              </Panel>
            </div>
          </div>

        </div>
      ) : null}
    </div>
  );
};

export default FunnelAnalysis;
